import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2, Circle, Loader2, AlertTriangle, Info, ExternalLink, Send, RotateCcw, Undo2,
} from 'lucide-react';
import ChangeSummary, { changeLabel } from './InlineChangeSummary';
import InlineUndoButton, { undoStatusText } from './InlineUndoButton';
import {
  publishSteps, formatElapsed, pluralize, softWarnings, failureText, liveUrl, describeReasons,
} from './inlineEdit';

// The publishing stages of the Visual Editor panel (trial feedback A):
// review → progress → success / under review / failed. The panel
// (InlineEditPanel) owns the flow (useInlinePublish); these only render it.

export const focusRing = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B0FC38]/60';
export const primaryBtn = 'w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[#B0FC38] text-gray-900 text-sm font-semibold hover:bg-[#9BE22E] shadow-lg shadow-[#B0FC38]/20 disabled:opacity-50 disabled:cursor-not-allowed';
export const secondaryBtn = 'w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full border border-white/[0.18] bg-white/[0.02] text-sm text-white hover:border-white/[0.35] disabled:opacity-50';
const tertiaryBtn = 'self-center px-2 py-1 rounded text-[12px] text-gray-400 underline hover:text-white';
const headingClasses = 'text-lg font-semibold text-white tracking-tight rounded';

function useHeadingFocus() {
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);
  return ref;
}

export function StageShell({ children, footer, testId }) {
  return (
    <div className="flex-1 flex flex-col overflow-hidden" data-testid={testId}>
      <div className="flex-1 overflow-y-auto p-5 flex flex-col gap-4">{children}</div>
      {footer && <div className="p-4 border-t border-white/[0.05] bg-white/[0.01] flex flex-col gap-2">{footer}</div>}
    </div>
  );
}

// ---- REVIEW ----

function CheckLine({ status, where, reasons, onCheck, checkLabel }) {
  if (status === 'fits') {
    return where === 'phones' ? <p className="text-[11px] text-green-300" data-testid="phone-check">Fits on phones ✓</p> : null;
  }
  if (status === 'checking') {
    return (
      <p className="text-[11px] text-gray-400 flex items-center gap-1.5" data-testid={where === 'phones' ? 'phone-check' : undefined}>
        <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" /> {where === 'phones' ? 'Checking phones…' : 'Checking…'}
      </p>
    );
  }
  if (status === 'blocked') {
    return (
      <div className="text-[11px] text-red-200/90 flex flex-col gap-0.5" data-testid={where === 'phones' ? 'phone-check' : undefined}>
        <p className="font-medium">Doesn’t fit on {where} — fix or remove</p>
        {describeReasons(reasons).map((r) => <p key={r}>{r}</p>)}
      </div>
    );
  }
  return (
    <p className="text-[11px] text-gray-400 flex flex-wrap items-center gap-x-2" data-testid={where === 'phones' ? 'phone-check' : undefined}>
      <span>Not checked on {where} yet.</span>
      <button type="button" onClick={onCheck} className={`${focusRing} underline text-[#B0FC38] hover:text-white`}>{checkLabel}</button>
    </p>
  );
}

function ReviewItem({ entry, status, upload, siteOrigin, onRemove, onCheck }) {
  const { change, measures } = entry;
  const headingId = `inline-review-${change.id}`;
  const shown = change.kind === 'image'
    ? {
      ...change,
      after: { ...change.after, alt: upload?.decorative ? '' : (upload?.alt ?? change.after?.alt), filename: upload?.filename || change.after?.filename },
      decorative: upload?.decorative ?? change.decorative,
    }
    : change;
  const soft = softWarnings(measures);
  const label = changeLabel(change);
  return (
    <li>
      <article aria-labelledby={headingId} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3 flex flex-col gap-2" data-testid="review-item">
        <ChangeSummary change={shown} siteOrigin={siteOrigin} previewSrc={upload?.dataUrl} headingId={headingId} />
        <CheckLine status={status.phone} where="phones" reasons={measures?.phone?.reasons} onCheck={() => onCheck(entry, 'phone')} checkLabel="Check on Phone" />
        <CheckLine status={status.desktop} where="larger screens" reasons={measures?.desktop?.reasons} onCheck={() => onCheck(entry, 'desktop')} checkLabel="Check on Desktop" />
        {soft.map((t) => <p key={t} className="text-[11px] text-yellow-200/90" data-testid="soft-warning">{t}</p>)}
        <div className="flex justify-end">
          <button type="button" onClick={() => onRemove(change.id)} aria-label={`Remove ${label.toLowerCase()} change on ${change.path}`}
            className={`${focusRing} inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/[0.08] text-[11px] text-gray-300 hover:text-white hover:border-white/[0.2]`}>
            Remove
          </button>
        </div>
      </article>
    </li>
  );
}

export function ReviewStage({ entries, statusOf, uploads, siteOrigin, disabledReason, onRemove, onCheck, onPublish, onKeepEditing }) {
  const headingRef = useHeadingFocus();
  return (
    <StageShell
      testId="inline-stage-review"
      footer={(
        <>
          <button type="button" onClick={onPublish} disabled={!!disabledReason}
            aria-describedby={disabledReason ? 'inline-publish-hint' : undefined} className={`${focusRing} ${primaryBtn}`}>
            <Send className="w-3.5 h-3.5" aria-hidden="true" /> Publish to my website
          </button>
          {disabledReason && <p id="inline-publish-hint" className="text-[11px] text-gray-500 text-center">{disabledReason}</p>}
          <button type="button" onClick={onKeepEditing} className={`${focusRing} ${secondaryBtn}`}>Keep editing</button>
        </>
      )}
    >
      <h2 ref={headingRef} tabIndex={-1} className={`${headingClasses} ${focusRing}`}>
        Publish {pluralize(entries.length, 'change')} to your website?
      </h2>
      <ul className="flex flex-col gap-2">
        {entries.map((entry) => (
          <ReviewItem key={entry.change.id} entry={entry} status={statusOf(entry)} upload={uploads?.get?.(entry.change.id)}
            siteOrigin={siteOrigin} onRemove={onRemove} onCheck={onCheck} />
        ))}
      </ul>
      <p className="text-[12px] text-gray-400">Only these words and pictures change. The design stays the same.</p>
    </StageShell>
  );
}

// ---- PROGRESS ----

const STEP_STATE_WORDS = { done: 'done', current: 'in progress', todo: 'not started yet' };

export function ProgressStage({ er, accepted, confirming, startedAt, lostContact }) {
  const headingRef = useHeadingFocus();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const steps = publishSteps({ accepted, publishState: er?.publishState || null, progress: er?.publishProgress || null });
  const lastDone = [...steps].reverse().find((s) => s.state === 'done');
  const announce = lastDone ? `${lastDone.label}${lastDone.detail ? `: ${lastDone.detail}` : ''}.` : '';
  return (
    <StageShell testId="inline-stage-progress">
      <h2 ref={headingRef} tabIndex={-1} className={`${headingClasses} ${focusRing}`}>Publishing your changes</h2>
      <ol className="flex flex-col gap-3" data-testid="publish-steps">
        {steps.map((s) => (
          <li key={s.key} data-state={s.state} className="flex items-start gap-2.5 text-[13px]">
            {s.state === 'done' && <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-green-300" aria-hidden="true" />}
            {s.state === 'current' && <Loader2 className="w-4 h-4 mt-0.5 shrink-0 text-[#B0FC38] animate-spin" aria-hidden="true" />}
            {s.state === 'todo' && <Circle className="w-4 h-4 mt-0.5 shrink-0 text-gray-600" aria-hidden="true" />}
            <span className={s.state === 'todo' ? 'text-gray-500' : 'text-white'}>
              {s.label}
              <span className="sr-only"> ({STEP_STATE_WORDS[s.state]})</span>
              {s.detail && <span className="text-gray-400"> · {s.detail}</span>}
            </span>
          </li>
        ))}
      </ol>
      {confirming && (
        <p className="flex items-center gap-2 text-[12px] text-gray-300" data-testid="publish-confirming">
          <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Checking your website shows the changes…
        </p>
      )}
      <p className="text-[12px] text-gray-400">
        Time so far: <span role="timer" data-testid="publish-elapsed">{formatElapsed(startedAt ? now - startedAt : 0)}</span>
      </p>
      <p className="text-[12px] text-gray-300 leading-relaxed">This usually takes 2–4 minutes. You can leave this page — the update keeps going.</p>
      {lostContact && (
        <p role="status" className="flex items-start gap-2 px-3 py-2 rounded-lg bg-yellow-400/[0.06] border border-yellow-400/25 text-[12px] text-yellow-100/90">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-yellow-300" aria-hidden="true" />
          We can’t reach NexSav right now. Your update keeps going on its own — we’ll keep checking.
        </p>
      )}
      <p className="sr-only" aria-live="polite" data-testid="publish-announcer">{announce}</p>
    </StageShell>
  );
}

// ---- SUCCESS ----

function PublishedList({ changes, previews, siteOrigin, label }) {
  if (!changes.length) return null;
  return (
    <section aria-label={label} className="flex flex-col gap-2">
      <h3 className="text-[10px] font-medium text-gray-500 tracking-wider uppercase">{label}</h3>
      <ul className="flex flex-col gap-2">
        {changes.map((c) => (
          <li key={c.id} className="rounded-xl bg-white/[0.02] border border-white/[0.06] p-3">
            <ChangeSummary change={c} siteOrigin={siteOrigin} previewSrc={previews?.get?.(c.id)} />
          </li>
        ))}
      </ul>
    </section>
  );
}

function LivePageLink({ href }) {
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={`${focusRing} ${primaryBtn}`}>
      View your live page <ExternalLink className="w-3.5 h-3.5" aria-hidden="true" />
      <span className="sr-only">(opens in a new tab)</span>
    </a>
  );
}

function undoLine(undo, undoConfirm) {
  if (undo.state === 'undoing') return { tone: 'busy', text: 'Undoing… This usually takes a few minutes.' };
  if (undo.state === 'reverted') {
    if (undoConfirm === 'checking' || undoConfirm === 'idle') return { tone: 'busy', text: 'Undoing… checking your website.' };
    if (undoConfirm === 'verified') return { tone: 'ok', text: 'Undone — your website shows the previous version again.' };
    return { tone: 'ok', text: 'Undone. It can take a minute to appear everywhere — refresh your site if you still see the new version.' };
  }
  if (undo.state === 'failed') return { tone: 'bad', text: undoStatusText('failed', undo.message) };
  return null;
}

export function SuccessStage({ flow, siteOrigin, onMakeMore }) {
  const headingRef = useHeadingFocus();
  const { published, previews, confirm, er, request, undo, undoConfirm, undoResume, onUndoState } = flow;
  const href = liveUrl(siteOrigin, published[0]?.path || '/');
  const canUndo = !!request?.id && (!!er?.mergeCommitSha || undoResume);
  const undone = undo.state === 'reverted' && undoConfirm !== 'checking' && undoConfirm !== 'idle';
  const undoingNow = undo.state === 'undoing' || (undo.state === 'reverted' && !undone);
  const line = undoLine(undo, undoConfirm);
  let heading = 'Your website is updated';
  if (undoingNow) heading = 'Undoing this publish…';
  else if (undone) heading = 'This publish was undone';
  return (
    <StageShell
      testId="inline-stage-success"
      footer={(
        <>
          <LivePageLink href={href} />
          {canUndo && undo.state !== 'reverted' && (
            <InlineUndoButton requestId={request.id} hideStatus onStateChange={onUndoState}
              initialState={undoResume ? 'undoing' : undefined}
              buttonClassName={secondaryBtn} />
          )}
          <p role="status" aria-live="polite" data-testid="undo-status"
            className={line ? `text-[12px] leading-relaxed ${line.tone === 'bad' ? 'text-red-300' : line.tone === 'ok' ? 'text-green-300' : 'text-gray-300'}` : 'sr-only'}>
            {line?.tone === 'busy' && <Loader2 className="inline w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />}
            {line?.text || ''}
          </p>
          <button type="button" onClick={onMakeMore} disabled={undoingNow} className={`${focusRing} ${tertiaryBtn} disabled:opacity-50`}>Make more changes</button>
        </>
      )}
    >
      <div className="flex flex-col items-center text-center gap-3 pt-2">
        {undone || undoingNow
          ? <Undo2 className="w-12 h-12 text-gray-300" aria-hidden="true" />
          : <CheckCircle2 className="w-14 h-14 text-green-400" aria-hidden="true" />}
        <h2 ref={headingRef} tabIndex={-1} className={`${headingClasses} ${focusRing}`}>{heading}</h2>
        {!undone && !undoingNow && (
          <p className="text-[13px] text-gray-300 leading-relaxed" data-testid="success-lead">
            {confirm === 'verified'
              ? 'Your website now shows these changes.'
              : 'It can take a minute to appear everywhere — refresh your site if you don’t see it yet.'}
          </p>
        )}
      </div>
      <PublishedList changes={published} previews={previews} siteOrigin={siteOrigin} label="What changed" />
    </StageShell>
  );
}

// ---- UNDER REVIEW ----

// The wording of a publish that stopped for a person, by how far it got:
// `applied` of `total` changes were published; `pageUpdate` (useInlinePublish)
// says whether the page is still waiting for them to be deployed.
//   waiting  "We published 3 of 4 changes." + "Updating the page…"
//   live     "3 of 4 changes are live." + "1 needs a NexSav team member, who will finish it."
//   timeout  "We published 3 of 4 changes." + "may take a minute to appear" + the rest
//   nothing applied: "Nothing was published yet." + "A NexSav team member will finish this."
export function underReviewText({ applied, total, pageUpdate }) {
  if (!applied) return { heading: 'Nothing was published yet.', lead: 'A NexSav team member will finish this.' };
  const left = Math.max(0, total - applied);
  const rest = left === 0
    ? 'A NexSav team member will finish this.'
    : `${left} ${left === 1 ? 'needs' : 'need'} a NexSav team member, who will finish ${left === 1 ? 'it' : 'them'}.`;
  const published = `We published ${applied} of ${pluralize(total, 'change')}.`;
  if (pageUpdate === 'live') {
    return { heading: `${applied} of ${pluralize(total, 'change')} ${applied === 1 ? 'is' : 'are'} live.`, lead: rest };
  }
  if (pageUpdate === 'timeout') {
    return {
      heading: published,
      lead: `The published ${applied === 1 ? 'change' : 'changes'} may take a minute to appear on your website. ${rest}`,
    };
  }
  return { heading: published, lead: rest };
}

export function UnderReviewStage({ flow, siteOrigin, onMakeMore }) {
  const headingRef = useHeadingFocus();
  const changes = flow.published;
  const applied = changes.filter((c) => c.result?.status === 'applied');
  const waiting = changes.filter((c) => c.result?.status !== 'applied');
  const updating = flow.pageUpdate === 'waiting';
  const { heading, lead } = underReviewText({ applied: applied.length, total: changes.length, pageUpdate: flow.pageUpdate });
  const settledAfterWait = flow.pageUpdate === 'live' || flow.pageUpdate === 'timeout';
  return (
    <StageShell
      testId="inline-stage-under-review"
      footer={(
        <>
          {applied.length > 0 && <LivePageLink href={liveUrl(siteOrigin, applied[0].path || '/')} />}
          <button type="button" onClick={onMakeMore} disabled={updating}
            aria-describedby={updating ? 'inline-under-review-wait' : undefined}
            className={`${focusRing} ${secondaryBtn}`}>
            Make more changes
          </button>
          {updating && (
            <p id="inline-under-review-wait" className="text-[11px] text-gray-500 text-center">
              You can make more changes once the page is updated.
            </p>
          )}
        </>
      )}
    >
      <div className="flex items-start gap-3">
        <Info className="w-5 h-5 shrink-0 mt-1 text-yellow-300" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <h2 ref={headingRef} tabIndex={-1} className={`${headingClasses} ${focusRing}`}>{heading}</h2>
          <p className="text-[13px] text-gray-300 leading-relaxed" data-testid="under-review-lead">{lead}</p>
        </div>
      </div>
      <p role="status" aria-live="polite" data-testid="under-review-update"
        className={updating ? 'flex items-center gap-2 text-[12px] text-gray-300' : 'sr-only'}>
        {updating && <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />}
        {updating ? 'Updating the page…' : (settledAfterWait ? `${heading} ${lead}` : '')}
      </p>
      <PublishedList changes={applied} previews={flow.previews} siteOrigin={siteOrigin} label="Published" />
      <PublishedList changes={waiting} previews={flow.previews} siteOrigin={siteOrigin} label="Waiting for a NexSav team member" />
    </StageShell>
  );
}

// ---- FAILED ----

export function FailedStage({ outcome, canRetry, onRetry, onKeepEditing, hasChanges }) {
  const headingRef = useHeadingFocus();
  const why = outcome?.message || failureText(outcome?.reason) || null;
  return (
    <StageShell
      testId="inline-stage-failed"
      footer={(
        <>
          {canRetry && (
            <button type="button" onClick={onRetry} className={`${focusRing} ${primaryBtn}`}>
              <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" /> Try again
            </button>
          )}
          <Link to="/portal/edits" state={{ openForm: true }} className={`${focusRing} ${secondaryBtn}`}>Send as an edit request instead</Link>
          {hasChanges && (
            <button type="button" onClick={onKeepEditing} className={`${focusRing} ${tertiaryBtn}`}>Keep editing</button>
          )}
        </>
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 shrink-0 mt-1 text-red-300" aria-hidden="true" />
        <div className="flex flex-col gap-1.5">
          <h2 ref={headingRef} tabIndex={-1} className={`${headingClasses} ${focusRing}`}>
            We couldn’t publish this time — nothing on your website changed.
          </h2>
          {why && <p className="text-[13px] text-gray-300 leading-relaxed" data-testid="failed-reason">{why}</p>}
        </div>
      </div>
    </StageShell>
  );
}
